import { HgSupportedLanguage } from '@smg-real-estate/domus';
import type { GeoLocation } from '@smg-real-estate/geo-libs';

import { useGeoStore } from '@/stores/geo';

/** Returns the canonical geo id (`newId`) for a resolved location. */
export const getLocationGeoId = (location: GeoLocation) => location.newId;

/**
 * Collects parent location ids (excluding zip-code and country segments) so
 * breadcrumbs can resolve ancestor locations in a single batch.
 */
export const extractParentLocationIds = (locations: GeoLocation[]): string[] => {
  const parentLocationIds: string[] = [];
  locations.forEach((location) => {
    if (location.parentPaths && location.parentPaths.length > 0) {
      const parentPath = location.parentPaths[0];
      const parentIds = parentPath.filter(
        (id: string) => !id.startsWith('geo-zipcode') && !id.startsWith('geo-country'),
      );
      parentLocationIds.push(...parentIds);
    }
  });
  return parentLocationIds;
};

/**
 * Resolves a mixed list of geo ids (`geo-…`) and url names into `GeoLocation[]`,
 * preserving the input order, and pre-resolves their parent locations for
 * breadcrumbs. Shared by the agency and agent directory stores.
 */
export const resolveGeoLocations = async (
  geoLocations: string[],
  locale: HgSupportedLanguage,
): Promise<GeoLocation[]> => {
  const geoStore = useGeoStore();
  const locationIds = geoLocations.filter((loc) => loc.includes('geo-'));
  const urlNames = geoLocations.filter((loc) => !loc.includes('geo-'));

  // Resolve locations by ID using geo store
  if (locationIds.length > 0) {
    await geoStore.resolveLocationsById(locationIds);
  }

  // Resolve locations by URL name using geo store
  if (urlNames.length > 0) {
    await geoStore.resolveLocationsByUrlName(urlNames, locale);
  }

  // Get resolved locations from geo store in the same order as input
  const resolvedLocations: GeoLocation[] = geoLocations
    .map((loc) => {
      if (loc.includes('geo-')) {
        return geoStore.getGeoLocationById(loc);
      } else {
        return geoStore.getGeoLocationByUrlName(loc, locale);
      }
    })
    .filter((loc): loc is GeoLocation => loc !== undefined);

  // Also resolve parent locations for breadcrumbs
  const parentLocationIds = extractParentLocationIds(resolvedLocations);
  if (parentLocationIds.length > 0) {
    await geoStore.resolveLocationsById(parentLocationIds);
  }

  return resolvedLocations;
};

/** Builds the comma-separated `loc` query param for the extra (non-primary) locations. */
export const buildExtraLocationsQueryParam = (locations: GeoLocation[]): string =>
  locations
    .slice(1)
    .map((location) => getLocationGeoId(location) ?? '')
    .filter((id) => !!id)
    .join(',');
